import React, { useMemo, useCallback, ReactElement } from 'react';
import Table from '../../../../../../components/table/table.component';
import TableBody from '../../../../../../components/table-body/table-body.component';
import TableRow from '../../../../../../components/table-row/table-row.component';
import TableCell from '../../../../../../components/table-cell/table-cell.component';
import Chip from '../../../../../../components/chip/chip.component';
import CheckAccess from '../../../../../../components/check-access/check-access.component';
import FormFieldMultiSelect
  from '../../../../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldCheckbox from '../../../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../../../../services/format-service';
import StructureService from '../../../../../../services/structure-service';
import {
  SETTING_NAME_ACTIVITY_COMMENT_USER_MENTIONS, SETTING_NAME_CONTACT_COMMENT_USER_MENTIONS,
  SETTING_NAME_DEAL_TASK_COMMENT_USER_MENTIONS,
} from '../../../../../../constants/entity/setting-names';
import { EMAIL_NOTIFICATION_LIST_VIEW } from '../../../../../../constants/entity/permissions';
import AssetDefaultImage from '../../../../../../static/images/asset-default-image.png';

const SettingsUserNotificationsEmail: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COMMON_EMAIL_NOTIFICATION_TYPE_LIST: string[] = useMemo((): string[] => [
    SETTING_NAME_ACTIVITY_COMMENT_USER_MENTIONS,
    SETTING_NAME_DEAL_TASK_COMMENT_USER_MENTIONS,
    SETTING_NAME_CONTACT_COMMENT_USER_MENTIONS,
  ], []);

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    visibleOptions,
    notificationSettingsData,
    selectedDeals,
    selectedPortfolios,
    dealList,
    portfolioList,
    onCancelForm,
    onSubmitForm,
    onOptionDealsChange,
    onOptionPortfoliosChange,
    onVisibleOptionsChange,
    onDealSearch,
    onDealSearchClose,
  } = props;

  const formPrefix: string = 'notificationSettings';

  const emailNotificationTypesFilterPermissions: string[] = useMemo((): string[] => [EMAIL_NOTIFICATION_LIST_VIEW], []);

  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioList && portfolioList.length ? portfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioItemList: number[]): boolean => (
        portfolioItemList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [portfolioList]);

  const onDealValuesChange: Function = useCallback((dealValues: Deal[], settingItem: Setting): void => {
    if (onOptionDealsChange) {
      onOptionDealsChange(dealValues, `${formPrefix}[${settingItem.name}]`, settingItem.id);
    }
  }, [formPrefix, selectedDeals]);

  const onPortfolioValuesChange: Function = useCallback((
    portfolioValues: Portfolio[], settingItem: Setting,
  ): void => {
    if (onOptionPortfoliosChange) {
      onOptionPortfoliosChange(portfolioValues, `${formPrefix}[${settingItem.name}]`, settingItem.id);
    }
  }, [formPrefix, selectedPortfolios]);

  const onSettingValueChange: Function = useCallback((
    settingId: number, settingName: string, value: SettingValue,
  ): void => {
    if (value) {
      if (!visibleOptions.includes(settingId) && onVisibleOptionsChange) {
        onVisibleOptionsChange([...visibleOptions, settingId]);
      }
    } else if (visibleOptions.includes(settingId)) {
      const visibleOptionIndex: number = visibleOptions.indexOf(settingId);

      if (visibleOptionIndex !== FormatService.INDEX_NOT_FOUND) {
        const newVisibleOptions: number[] = [...visibleOptions];

        StructureService.deleteListItemByIndex(newVisibleOptions, visibleOptionIndex);

        if (onVisibleOptionsChange) {
          onVisibleOptionsChange(newVisibleOptions);
        }

        const notificationSetting: Setting = notificationSettingsData.find((settingItem: Setting): boolean => (
          settingItem.id === settingId
        ));

        if (onOptionDealsChange) {
          onOptionDealsChange(
            notificationSetting.options && notificationSetting.options.dealIds ? [] : null,
            settingName,
            settingId,
          );
        }

        if (onOptionPortfoliosChange) {
          onOptionPortfoliosChange(
            notificationSetting.options && notificationSetting.options.portfolioIds ? [] : null,
            settingName,
            settingId,
          );
        }
      }
    }
  }, [visibleOptions, notificationSettingsData, selectedDeals, selectedPortfolios]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      isEmpty={!notificationSettingsData || !notificationSettingsData.length}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        {
          notificationSettingsData && !!notificationSettingsData.length
          && notificationSettingsData.map((settingItem: Setting): ReactElement => (
            <CheckAccess
              key={settingItem.id}
              permissions={!COMMON_EMAIL_NOTIFICATION_TYPE_LIST.includes(settingItem.name)
                ? emailNotificationTypesFilterPermissions : []}
            >
              <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                <TableRow>
                  <TableCell>
                    {settingItem.title}
                  </TableCell>
                  <TableCell>
                    {
                      isEditMode ? (
                        <FormFieldCheckbox
                          withNoLabelOffset
                          name={`${formPrefix}[${settingItem.name}].value`}
                          onChange={((value: SettingValue): void => {
                            onSettingValueChange(
                              settingItem.id,
                              `${formPrefix}[${settingItem.name}]`,
                              value,
                            );
                          })}
                        />
                      ) : (
                        <div>
                          {FormatService.formatBooleanValue(settingItem.value)}
                        </div>
                      )
                    }
                  </TableCell>
                </TableRow>
                {
                  settingItem.options && settingItem.options.dealIds
                  && (isEditMode || !!settingItem.options.dealIds.length)
                  && ((selectedPortfolios && ((selectedPortfolios[settingItem.id]
                      && !selectedPortfolios[settingItem.id].length) || !selectedPortfolios[settingItem.id]))
                    || !selectedPortfolios)
                  && visibleOptions && visibleOptions.includes(settingItem.id) && (
                    <TableRow>
                      <TableCell>
                        Deals:
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldMultiSelect
                              isBordered
                              withImage
                              withSearch
                              isDisabled={dealList && !dealList.length}
                              imageThumbnailKey="mainPhotoThumbnail"
                              imageKey="mainPhoto"
                              name={`${formPrefix}[${settingItem.name}].options.dealIds`}
                              defaultImage={AssetDefaultImage}
                              optionList={dealList}
                              onSearch={onDealSearch}
                              onClose={onDealSearchClose}
                              onValuesChange={(dealValues: Deal[]): void => {
                                onDealValuesChange(dealValues, settingItem);
                              }}
                            >
                              {
                                MenuItemList({
                                  withImage: true,
                                  isExtended: true,
                                  optionItemLabelKey: 'name',
                                  optionList: dealList,
                                  optionItemImageKey: 'mainPhoto',
                                  optionItemImageThumbnailKey: 'mainPhotoThumbnail',
                                  defaultImage: AssetDefaultImage,
                                  selectedItemList: selectedDeals && selectedDeals[settingItem.id],
                                })
                              }
                            </FormFieldMultiSelect>
                          ) : (
                            settingItem.options.dealIds.map((optionDealId: number): ReactElement | boolean => {
                              const deal: Deal | null = dealList && dealList.length
                                ? dealList.find((dealItem: Deal): boolean => dealItem.id === optionDealId) : null;

                              return !!deal && (
                                <Chip
                                  isDisabled
                                  withImageOffset
                                  key={deal.id}
                                  label={deal.name}
                                  imageLink={deal.mainPhotoThumbnail || deal.mainPhoto || AssetDefaultImage}
                                />
                              );
                            })
                          )
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  settingItem.options && settingItem.options.portfolioIds
                  && (isEditMode || !!settingItem.options.portfolioIds.length)
                  && ((selectedDeals && ((selectedDeals[settingItem.id] && !selectedDeals[settingItem.id].length)
                    || !selectedDeals[settingItem.id])) || !selectedDeals)
                  && visibleOptions && visibleOptions.includes(settingItem.id) && (
                    <TableRow>
                      <TableCell>
                        Portfolios:
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldMultiSelect
                              isBordered
                              isDisabled={portfolioList && !portfolioList.length}
                              name={`${formPrefix}[${settingItem.name}].options.portfolioIds`}
                              optionList={portfolioList}
                              onValuesChange={(portfolioValues: Portfolio[]): void => {
                                onPortfolioValuesChange(portfolioValues, settingItem);
                              }}
                            >
                              {
                                MenuItemList({
                                  isGrouped: isMultiplePortfolioGroupView,
                                  groupKey: 'group',
                                  groupItemLabelKey: 'name',
                                  optionItemLabelKey: 'name',
                                  optionList: portfolioList,
                                  selectedItemList: selectedPortfolios && selectedPortfolios[settingItem.id],
                                })
                              }
                            </FormFieldMultiSelect>
                          ) : (
                            settingItem.options.portfolioIds.map((
                              optionPortfolioId: number,
                            ): ReactElement | boolean => {
                              const portfolio: Portfolio | null = portfolioList && portfolioList.length
                                ? portfolioList.find((portfolioItem: Portfolio): boolean => (
                                  portfolioItem.id === optionPortfolioId
                                )) : null;

                              return !!portfolio && (
                                <Chip
                                  isDisabled
                                  key={portfolio.id}
                                  label={portfolio.name}
                                />
                              );
                            })
                          )
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </CheckAccess>
          ))
        }
      </Table>
    </SinglePageTab>
  );
});

export default SettingsUserNotificationsEmail;
